import { Chip, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxOrderDetailDto } from "@/api/diagnostics";
import { customersApi } from "@/api/endpoints";
import { ResolvedIdField } from "@/components/ResolvedIdField";

/**
 * Diagnostic order id, with the order echoed back. This is the identifier that repeats across
 * every diagnostics workbench screen, and the one that carries a patient — so a wrong paste here
 * means collecting a sample, filing a result or dispatching a report against the wrong person.
 *
 * Resolving it and showing accession number, patient and tests turns that from a silent error into
 * one the operator can see before they press the button.
 */
export function DiagnosticOrderIdField({
  value, onChange, label = "Diagnostic order", required, disabled, sx, onResolved,
}: {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  sx?: object;
  /**
   * Fired with the resolved order. Use this — not `onChange` — when you need the order's **id**:
   * the field also accepts an accession number, so the typed value is not always a Guid.
   */
  onResolved?: (order: DxOrderDetailDto | null) => void;
}) {
  return (
    <ResolvedIdField<DxOrderDetailDto>
      value={value}
      onChange={onChange}
      label={label}
      required={required}
      disabled={disabled}
      sx={sx}
      onResolved={onResolved}
      queryKey="dx-order-resolve"
      placeholder="Order id or accession number"
      resolve={(id) => diagnosticsApi.getOrder(id)}
      render={(o) => <OrderIdentity order={o} />}
    />
  );
}

/** The line the operator checks against the requisition or label in their hand. */
function OrderIdentity({ order }: { order: DxOrderDetailDto }) {
  // The order carries a customer id; the name and UHID are what a human recognises.
  const patient = useQuery({
    queryKey: ["customer", order.patientId],
    queryFn: () => customersApi.get(order.patientId),
    enabled: !!order.patientId,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const tests = order.items.map(i => i.displayName).filter(Boolean);

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600}>
          {patient.data?.name ?? "(patient not resolved)"}
        </Typography>
        {patient.data?.uhid && (
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{patient.data.uhid}</Typography>
        )}
        <Chip size="small" variant="outlined" label={order.accessionNumber} />
        <Chip size="small" label={order.status} />
        {order.priority !== "Routine" && <Chip size="small" color="warning" label={order.priority} />}
      </Stack>
      {tests.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {tests.slice(0, 4).join(", ")}{tests.length > 4 ? ` +${tests.length - 4} more` : ""}
        </Typography>
      )}
    </Stack>
  );
}
